/**
 * Renders feed layers from the history buffer while the timeline is in
 * historical mode, and advances the cursor during playback. Live rendering is
 * gated off in use-track-layers while historical, so the two never fight.
 */

import { useEffect } from 'react';
import type { Track } from '@vtak/shared';
import type { MapInstance, ViewMode } from '@/map/map-setup';
import { usePluginStore } from '@/stores/plugin-store';
import { usePluginDataStore } from '@/stores/plugin-data-store';
import { useLayerVisibilityStore } from '@/stores/layer-visibility-store';
import { useTimelineStore } from '@/stores/timeline-store';
import { buildActiveMapLayers } from '@/components/map/plugin-layer-manager';
import { renderLayer } from '@/map/markers/track-layer-controller';
import { snapshotAt, earliestSample } from '@/map/history/history-buffer';
import { serverSnapshotAt } from '@/map/history/server-history';

/** Playback tick cadence. */
const TICK_MS = 500;

/** Drive historical rendering + playback for the timeline. */
export function useHistoryPlayback(
  mapRef: React.RefObject<MapInstance | null>,
  modeRef: React.RefObject<ViewMode>,
): void {
  useEffect(() => {
    const unsub = useTimelineStore.subscribe((state, prev) => {
      if (state.mode === 'historical') {
        if (state.cursor !== prev.cursor || state.mode !== prev.mode) {
          renderSet(mapRef, modeRef, (layer) => historicalData(layer, state.cursor, rerender));
        }
      } else if (prev.mode === 'historical') {
        renderSet(mapRef, modeRef, liveData);
      }
    });
    return unsub;
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      const timeline = useTimelineStore.getState();
      if (timeline.mode === 'historical' && timeline.playing) timeline.advance(TICK_MS);
    }, TICK_MS);
    return () => clearInterval(id);
  }, []);
}

/** Render every visible feed layer using the supplied per-layer data. */
function renderSet(
  mapRef: React.RefObject<MapInstance | null>,
  modeRef: React.RefObject<ViewMode>,
  dataFor: (layer: string) => Track[],
): void {
  const map = mapRef.current;
  if (!map) return;
  for (const layer of visibleFeedLayers()) {
    renderLayer(map, modeRef.current, layer, dataFor(layer), true);
  }
}

/** Current live data for a layer. */
function liveData(layer: string): Track[] {
  return (usePluginDataStore.getState().data.get(layer) as Track[]) ?? [];
}

/**
 * Historical data for a layer at the cursor: the in-browser buffer when the
 * cursor is within the live session, else the server history snapshot (deep
 * replay of recorded mission sessions). `onArrive` re-renders once a server
 * bucket lands.
 */
function historicalData(layer: string, cursor: number, onArrive: () => void): Track[] {
  if (cursor >= earliestSample(Date.now())) return snapshotAt(layer, cursor);
  return serverSnapshotAt(layer, cursor, onArrive) ?? [];
}

/** Nudge the timeline so the subscriber re-renders at the current cursor. */
function rerender(): void {
  const timeline = useTimelineStore.getState();
  if (timeline.mode === 'historical') timeline.scrubTo(timeline.cursor);
}

/** Full ids of currently-visible feed layers. */
function visibleFeedLayers(): string[] {
  const visibility = useLayerVisibilityStore.getState();
  return buildActiveMapLayers(usePluginStore.getState().activePlugins)
    .map((layer) => layer.fullId)
    .filter((id) => visibility.isVisible(id));
}
